/**
 * Explainer Registry
 *
 * Each explainer is a modular card that can be pulled (not pushed) during
 * a site visit. Three states per card:
 *   - viewed:   the engineer opened it
 *   - starred:  relevant to this design (⭐)
 *   - inReport: include in the PDF leave-behind (📄)
 *
 * The same registry drives:
 *   1. Site-visit mode  — cards you can open and mark
 *   2. PDF generator    — filters by starred/inReport
 *   3. Customer portal  — replays starred explainers
 */

// ── Explainer state ─────────────────────────────────────────────────

export type ExplainerState = 'unseen' | 'viewed' | 'starred' | 'in-report';

export interface Explainer {
  id: string;
  topic: string;
  title: string;
  description: string;
  /** Audience level — controls language complexity */
  audience: 'customer' | 'technical' | 'both';
  /** Other explainer IDs that should be viewed first */
  prerequisites: string[];
  /** Content blocks rendered in order */
  content: ExplainerContentBlock[];
  /** Appendix ID for the print system */
  appendixId?: string;
  /** Which tile(s) this relates to */
  relatedTiles: string[];

  // ── Mutable state (per-session) ──
  state: ExplainerState;
  /** When this explainer was last opened */
  viewedAt?: string;
  /** Display order in the session (order discussed) */
  discussionOrder?: number;
}

export interface ExplainerContentBlock {
  type: 'text' | 'diagram' | 'chart' | 'animation' | 'comparison';
  /** Markdown body for text blocks */
  body?: string;
  /** Component key for diagram/chart/animation blocks */
  componentKey?: string;
  /** Alt text or caption */
  caption?: string;
}

// ── Default explainer registry ──────────────────────────────────────

let _discussionCounter = 0;

export const DEFAULT_EXPLAINERS: Explainer[] = [
  // ── Energy & efficiency ───
  {
    id: 'pipe-sizing',
    topic: 'distribution',
    title: 'Why pipe sizing matters',
    description: 'How primary pipe diameter affects flow rate, velocity, and system performance.',
    audience: 'both',
    prerequisites: [],
    content: [
      {
        type: 'text',
        body: 'Primary pipes carry heated water from the boiler to emitters. Undersized pipes increase velocity and pressure drop, which reduces flow rate and forces the boiler to work harder. This wastes energy and shortens component life.',
      },
      {
        type: 'text',
        body: '**15 mm** — adequate for small systems or extensions only. **22 mm** — standard for most domestic systems. **28 mm** — recommended where heat pump or high-output systems need maximum flow capacity.',
      },
      { type: 'diagram', componentKey: 'PipeworkFlowChart', caption: 'Flow capacity vs pipe diameter' },
    ],
    appendixId: 'pipework-flow',
    relatedTiles: ['emitters'],
    state: 'unseen',
  },
  {
    id: 'energy-losses',
    topic: 'efficiency',
    title: 'Where energy is lost',
    description: 'Standing losses, distribution losses, and flue losses — the three places heat escapes before it reaches you.',
    audience: 'customer',
    prerequisites: [],
    content: [
      { type: 'text', body: 'Every heating system loses energy in three main places: the boiler flue (combustion losses), the pipes (distribution losses), and the cylinder (standing losses). Understanding where *your* system loses most helps target the right improvements.' },
      { type: 'diagram', componentKey: 'EnergyLossesGraphic', caption: 'Where heat goes before it reaches you' },
    ],
    appendixId: 'clean-systems',
    relatedTiles: ['system-details', 'water'],
    state: 'unseen',
  },
  {
    id: 'control-precision',
    topic: 'controls',
    title: 'Control precision',
    description: 'How your thermostat and controls affect boiler cycling, comfort, and fuel use.',
    audience: 'both',
    prerequisites: [],
    content: [
      { type: 'text', body: 'A mechanical thermostat has ±2–3 °C hysteresis — the boiler fires hard then coasts. A modulating thermostat with a load compensating thermostat adjusts flame intensity continuously, keeping the room within ±0.3 °C. Less cycling = less fuel, longer boiler life, better comfort.' },
      { type: 'chart', componentKey: 'ControlsModulationChart', caption: 'Cycling vs modulation comparison' },
    ],
    appendixId: 'modulation-cycling',
    relatedTiles: ['system-details'],
    state: 'unseen',
  },
  {
    id: 'flow-temperatures',
    topic: 'efficiency',
    title: 'Flow temperature & condensing',
    description: 'Why lower flow temperatures recover latent heat and dramatically improve efficiency.',
    audience: 'both',
    prerequisites: ['control-precision'],
    content: [
      { type: 'text', body: 'A condensing boiler only truly condenses when the return temperature is below ~54 °C. At typical 80/60 flow/return, efficiency is ~88%. Drop to 55/40 and you reach ~94%. Weather compensation does this automatically.' },
      { type: 'chart', componentKey: 'HeatOutputChart', caption: 'Efficiency vs flow temperature' },
    ],
    appendixId: 'modulation-cycling',
    relatedTiles: ['system-details', 'emitters'],
    state: 'unseen',
  },
  {
    id: 'heat-pump-behaviour',
    topic: 'heat-pumps',
    title: 'Heat pump behaviour',
    description: 'How a heat pump heats differently — slower, steadier, and why that matters.',
    audience: 'customer',
    prerequisites: ['flow-temperatures'],
    content: [
      { type: 'text', body: 'A heat pump delivers heat at lower intensity over longer periods. It\'s not broken — it\'s designed that way. Think of it as a marathon runner vs a sprinter. The house stays warm but recovery from cold is slower.' },
      { type: 'animation', componentKey: 'HeatPumpEnergyFlow', caption: 'How outdoor air becomes indoor heat' },
      { type: 'text', body: 'This is why fabric and emitter sizing matter more for heat pumps. If the house loses heat faster than the HP can replace it, comfort suffers.' },
    ],
    relatedTiles: ['heat-demand', 'emitters'],
    state: 'unseen',
  },

  // ── System layout ───
  {
    id: 'y-plan-vs-s-plan',
    topic: 'controls',
    title: 'Y-plan vs S-plan',
    description: 'How your motorised valves control heating and hot water circuits.',
    audience: 'technical',
    prerequisites: [],
    content: [
      { type: 'text', body: '**Y-plan** uses a single 3-port valve with a mid-position. It\'s simpler but the mid-position is a compromise — both circuits share one valve. **S-plan** uses two independent 2-port valves, giving true independent control.' },
      { type: 'animation', componentKey: 'YPlanValve', caption: 'Y-plan 3-port valve positions' },
      { type: 'animation', componentKey: 'SPlanValve', caption: 'S-plan independent 2-port valves' },
    ],
    relatedTiles: ['system-details'],
    state: 'unseen',
  },
  {
    id: 'sealed-vs-open-vented',
    topic: 'system-type',
    title: 'Sealed vs open-vented systems',
    description: 'The difference between a sealed pressurised system and an open-vented system with a feed & expansion tank.',
    audience: 'both',
    prerequisites: [],
    content: [
      { type: 'text', body: 'An **open-vented** system relies on a feed & expansion tank in the loft. It\'s self-regulating but introduces oxygen (promoting corrosion) and limits pressure. A **sealed** system uses an expansion vessel, runs at higher pressure, and eliminates the tank.' },
      { type: 'animation', componentKey: 'SealedVsOpenVented', caption: 'System layout comparison' },
      { type: 'text', body: 'Converting to sealed is often required for modern system boilers and always required for unvented cylinders.' },
    ],
    relatedTiles: ['system-details'],
    state: 'unseen',
  },
  {
    id: 'hydraulic-separation',
    topic: 'distribution',
    title: 'Hydraulic separation',
    description: 'What a low-loss header actually does (and what it doesn\'t).',
    audience: 'technical',
    prerequisites: ['pipe-sizing'],
    content: [
      { type: 'text', body: 'A **low-loss header (LLH)** creates a hydraulic neutral point between the boiler circuit and the system circuit. It protects the boiler pump from interference with the system pump. It does **not** add thermal mass, improve heat distribution, or act as a buffer.' },
      { type: 'animation', componentKey: 'HydraulicSeparation', caption: 'Primary and secondary loops' },
      { type: 'text', body: 'A **buffer vessel** is different — it adds thermal mass, which can mask short-cycling. But it doesn\'t fix the root cause (oversizing or poor modulation).' },
    ],
    relatedTiles: ['emitters'],
    state: 'unseen',
  },

  // ── Cost & longevity ───
  {
    id: 'cost-comparison',
    topic: 'cost',
    title: 'Cost & energy comparison',
    description: 'Running costs, upfront costs, and total cost of ownership across options.',
    audience: 'customer',
    prerequisites: [],
    content: [
      { type: 'chart', componentKey: 'AnnualCostChart', caption: 'Annual running cost comparison' },
      { type: 'chart', componentKey: 'CumulativeCostChart', caption: 'Total cost over 15 years' },
      { type: 'text', body: 'Running costs depend on fuel prices, efficiency, and usage pattern. Upfront costs vary widely. The best comparison is total cost of ownership over the expected life of the system.' },
    ],
    relatedTiles: [],
    state: 'unseen',
  },
  {
    id: 'longevity',
    topic: 'longevity',
    title: 'System longevity',
    description: 'What affects how long your heating system lasts and what shortens its life.',
    audience: 'customer',
    prerequisites: [],
    content: [
      { type: 'text', body: 'System life depends on water quality, cycling frequency, installation quality, and maintenance. A well-installed, well-maintained system boiler can last 15–20 years. Short-cycling, sludge, and poor water treatment can halve that.' },
      { type: 'diagram', componentKey: 'LongevityDiagram', caption: 'Factors affecting system lifespan' },
    ],
    relatedTiles: ['system-details'],
    state: 'unseen',
  },
  {
    id: 'power-flushing',
    topic: 'maintenance',
    title: 'Power flushing & filtration',
    description: 'When power flushing is needed, what it does, and what ongoing filtration prevents.',
    audience: 'both',
    prerequisites: [],
    content: [
      { type: 'text', body: 'Magnetite (black sludge) accumulates in systems over time. It blocks radiators, damages pump seals, and reduces boiler efficiency. Power flushing removes it. A magnetic filter prevents it from returning.' },
      { type: 'animation', componentKey: 'PowerFlush', caption: 'Bi-directional high-velocity flushing' },
      { type: 'animation', componentKey: 'LaminarFilter', caption: 'Continuous filtration in normal operation' },
    ],
    appendixId: 'power-flush-filters',
    relatedTiles: ['system-details'],
    state: 'unseen',
  },
  
  // ── Cold mains performance ───────────────────────────────────────
  {
    id: 'dhw-pressure-explained',
    topic: 'water-supply',
    title: 'Cold mains performance: why it matters',
    description: 'How cold mains flow/pressure affects combi and unvented suitability.',
    audience: 'both',
    prerequisites: [],
    content: [
      {
        type: 'text',
        body: 'Cold mains performance determines whether combi and unvented options can deliver acceptable hot water at outlets. This is separate from heating circuit pressure.',
      },
      { type: 'animation', componentKey: 'ColdMainsPerformance', caption: 'Impact of mains pressure on system options' },
      {
        type: 'text',
        body: 'Use measured values where possible: **flow (L/min)**, **static pressure (bar)**, and ideally a **dynamic pair (L/min @ bar under load)**. These are the core inputs for combi/unvented viability.',
      },
    ],
    relatedTiles: ['dhw-pressure', 'water'],
    state: 'unseen',
  },
  {
    id: 'regular-vs-system-boilers',
    topic: 'system-type',
    title: 'Regular vs system boilers',
    description: 'The only difference is heating circuit pressure — not hot water.',
    audience: 'both',
    prerequisites: ['sealed-vs-open-vented'],
    content: [
      {
        type: 'text',
        body: '**Regular boiler** — Open-vented heating circuit with F&E tank in loft. The boiler only heats water; expansion is handled by the tank.\n\n**System boiler** — Sealed heating circuit with expansion vessel and pressure gauge. The boiler contains pressurization components.',
      },
      { type: 'animation', componentKey: 'RegularVsSystemBoiler', caption: 'Regular vs system boiler circuit comparison' },
      {
        type: 'text',
        body: 'Both can serve **any DHW system**: vented cylinder, unvented cylinder, or thermal store. The choice between regular and system only affects the heating circuit (CH), not hot water.',
      },
    ],
    relatedTiles: ['system-details', 'dhw-pressure'],
    state: 'unseen',
  },
  {
    id: 'combi-mains-requirement',
    topic: 'water-supply',
    title: 'Why combis need mains pressure',
    description: 'Instant hot water requires high flow and pressure that gravity cannot provide.',
    audience: 'customer',
    prerequisites: ['dhw-pressure-explained'],
    content: [
      {
        type: 'text',
        body: 'A combi boiler heats water **on demand** — no storage. To deliver hot water instantly at usable pressure, it needs a strong incoming cold supply. Mains pressure (typically 2+ bar) provides this. Gravity from a loft tank cannot.',
      },
      { type: 'animation', componentKey: 'CombiMainsFlow', caption: 'Mains-fed vs gravity-fed combi comparison' },
      {
        type: 'text',
        body: 'If cold mains assessment shows weak capacity, vented cylinder options are usually more robust than combis.',
      },
    ],
    relatedTiles: ['dhw-pressure', 'system-details'],
    state: 'unseen',
  },
  {
    id: 'unvented-cylinder-requirements',
    topic: 'water-supply',
    title: 'Unvented cylinder requirements',
    description: 'Why unvented cylinders need good mains pressure and G3 certification.',
    audience: 'technical',
    prerequisites: ['dhw-pressure-explained'],
    content: [
      {
        type: 'text',
        body: 'An **unvented cylinder** is sealed and pressurized directly from the mains. It provides stored hot water at mains pressure — excellent for simultaneous outlets and shower performance.',
      },
      { type: 'animation', componentKey: 'UnventedCylinderDiagram', caption: 'Unvented cylinder with G3 safety components' },
      {
        type: 'text',
        body: 'If mains pressure is inadequate, an unvented cylinder will underperform. A vented cylinder (gravity-fed) is then more appropriate, even though delivery pressure will be lower.',
      },
    ],
    relatedTiles: ['dhw-pressure', 'water'],
    state: 'unseen',
  },
];

// ── Helpers ─────────────────────────────────────────────────────────

export function markViewed(explainers: Explainer[], id: string): Explainer[] {
  return explainers.map(e => {
    if (e.id !== id) return e;
    if (e.state === 'starred' || e.state === 'in-report') return e; // don't downgrade
    return {
      ...e,
      state: 'viewed' as const,
      viewedAt: new Date().toISOString(),
      discussionOrder: e.discussionOrder ?? ++_discussionCounter,
    };
  });
}

export function markStarred(explainers: Explainer[], id: string): Explainer[] {
  return explainers.map(e => {
    if (e.id !== id) return e;
    return {
      ...e,
      state: e.state === 'starred' ? 'viewed' as const : 'starred' as const,
      viewedAt: e.viewedAt || new Date().toISOString(),
      discussionOrder: e.discussionOrder ?? ++_discussionCounter,
    };
  });
}

export function markInReport(explainers: Explainer[], id: string): Explainer[] {
  return explainers.map(e => {
    if (e.id !== id) return e;
    return {
      ...e,
      state: e.state === 'in-report' ? 'starred' as const : 'in-report' as const,
      viewedAt: e.viewedAt || new Date().toISOString(),
      discussionOrder: e.discussionOrder ?? ++_discussionCounter,
    };
  });
}

/** Explainers marked ⭐ or 📄, sorted by discussion order */
export function getStarredExplainers(explainers: Explainer[]): Explainer[] {
  return explainers
    .filter(e => e.state === 'starred' || e.state === 'in-report')
    .sort((a, b) => (a.discussionOrder ?? 999) - (b.discussionOrder ?? 999));
}

/** Explainers marked 📄, sorted by discussion order */
export function getReportExplainers(explainers: Explainer[]): Explainer[] {
  return explainers
    .filter(e => e.state === 'in-report')
    .sort((a, b) => (a.discussionOrder ?? 999) - (b.discussionOrder ?? 999));
}

/** Serialisable snapshot for persistence (strips functions, keeps state) */
export function serializeExplainerState(explainers: Explainer[]): Array<{
  id: string;
  state: ExplainerState;
  viewedAt?: string;
  discussionOrder?: number;
}> {
  return explainers.map(e => ({
    id: e.id,
    state: e.state,
    viewedAt: e.viewedAt,
    discussionOrder: e.discussionOrder,
  }));
}

/** Rehydrate explainer state from a serialised snapshot */
export function rehydrateExplainerState(
  base: Explainer[],
  saved: Array<{ id: string; state: ExplainerState; viewedAt?: string; discussionOrder?: number }>,
): Explainer[] {
  const map = new Map(saved.map(s => [s.id, s]));
  return base.map(e => {
    const s = map.get(e.id);
    if (!s) return e;
    return { ...e, state: s.state, viewedAt: s.viewedAt, discussionOrder: s.discussionOrder };
  });
}
